import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { storage, db } from '../config/firebase';

/**
 * Upload image to Firebase Storage
 * Returns null if upload fails (CORS or other issues)
 * This is now completely optional and non-blocking
 */
export const uploadImageToStorage = async (file, userId = 'ces2026') => {
  try {
    const timestamp = Date.now();
    // Sanitize filename: remove spaces and special characters
    const sanitizedName = (file.name || 'business-card.jpg')
      .replace(/[^a-zA-Z0-9.-]/g, '_')
      .replace(/\s+/g, '_')
      .toLowerCase();
    const fileName = `${userId}/${timestamp}_${sanitizedName}`;
    const storageRef = ref(storage, `business-cards/${fileName}`);
    
    // Add timeout to prevent hanging
    const uploadPromise = uploadBytes(storageRef, file);
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Upload timeout')), 3000)
    );
    
    const snapshot = await Promise.race([uploadPromise, timeoutPromise]);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    // Silently fail for CORS issues or timeouts - we'll use local blob URL instead
    if (error.message?.includes('CORS') || 
        error.message?.includes('timeout') ||
        error.code === 'storage/unknown' ||
        error.message?.includes('ERR_FAILED')) {
      // Don't log CORS errors - they're expected in development
      return null;
    }
    // Only log unexpected errors
    console.warn('Firebase Storage upload issue:', error.message);
    return null;
  }
};

/**
 * Save lead data to Firestore (user-specific)
 */
export const saveLeadToFirestore = async (leadData, userId) => {
  if (!userId) {
    throw new Error('User ID is required to save lead');
  }

  try {
    // Save to user's subcollection: users/{userId}/leads/{leadId}
    const docRef = await addDoc(collection(db, 'users', userId, 'leads'), {
      ...leadData,
      userId: userId, // Also store userId in document for easier queries
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving lead:', error);
    throw new Error('Failed to save lead to database');
  }
};

/**
 * Update lead data in Firestore
 */
export const updateLeadInFirestore = async (leadId, userId, updatedData) => {
  if (!userId || !leadId) {
    throw new Error('User ID and Lead ID are required to update lead');
  }

  try {
    const leadRef = doc(db, 'users', userId, 'leads', leadId);
    await updateDoc(leadRef, {
      ...updatedData,
      updatedAt: serverTimestamp(),
    });
    
    return true;
  } catch (error) {
    console.error('Error updating lead:', error);
    throw new Error('Failed to update lead in database');
  }
};

/**
 * Delete lead from Firestore
 */
export const deleteLeadFromFirestore = async (leadId, userId) => {
  if (!userId || !leadId) {
    throw new Error('User ID and Lead ID are required to delete lead');
  }

  try {
    const leadRef = doc(db, 'users', userId, 'leads', leadId);
    await deleteDoc(leadRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting lead:', error);
    throw new Error('Failed to delete lead from database');
  }
};

